import { useTranslation } from 'react-i18next';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../app/pwa/customerFormDirtyGate.js';
import { Card } from '../../shared/ui/Card.js';
import { FormField, CustomerFormFieldBoundary } from '../../shared/ui/FormField.js';
import {
  FormErrorSummary,
  mapFieldErrorsToSummary,
} from '../../shared/ui/FormErrorSummary.js';
import { Button } from '../../shared/ui/Button.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useSignInScreen } from '../auth/hooks/useSignInScreen.js';
import { canMountEmbeddedSignInForm } from '../auth/routing/canMountEmbeddedSignInForm.js';
import { CUSTOMER_SIGN_IN_FIELD_IDS } from '../auth/customerAuthFieldIds.js';

export interface ReceiptLoginUpsellCardProps {
  readonly onAuthenticated: () => void;
  readonly onLoginStart?: () => void;
}

function ReceiptLoginUpsellCardBody({
  onAuthenticated,
  onLoginStart,
}: ReceiptLoginUpsellCardProps): JSX.Element {
  const { t } = useTranslation('customer');
  const signIn = useSignInScreen({
    onSignedIn: onAuthenticated,
    heading: t('receipt.loginCta'),
    helperText: t('receipt.saveToAccount'),
  });
  const isDraftDirty =
    signIn.identifier.trim().length > 0 || signIn.code.trim().length > 0;

  const requestSummary = mapFieldErrorsToSummary(
    { identifier: signIn.identifierError },
    { identifier: CUSTOMER_SIGN_IN_FIELD_IDS.identifier },
  );
  const verifySummary = mapFieldErrorsToSummary(
    { code: signIn.codeError },
    { code: CUSTOMER_SIGN_IN_FIELD_IDS.code },
  );

  return (
    <Card
      padded
      data-testid="customer-receipt-login-upsell"
      {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
    >
      <p className="text-sm text-[var(--color-on-surface)]">{t('receipt.saveToAccount')}</p>
      {signIn.challenge === null ? (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            onLoginStart?.();
            void signIn.handleRequest(event);
          }}
          noValidate
        >
          {/* G9/G16 — FormErrorSummary + invalid-only (no field errorText dual channel). */}
          <FormErrorSummary errors={requestSummary} />
          <FormField
            id={CUSTOMER_SIGN_IN_FIELD_IDS.identifier}
            label={signIn.identifierLabel}
            type="text"
            inputMode={signIn.identifierInputMode}
            autoComplete="username"
            value={signIn.identifier}
            onChange={(event) => signIn.setIdentifier(event.target.value)}
            invalid={Boolean(signIn.identifierError)}
            data-testid="customer-sign-in-identifier"
          />
          <Button
            intent="primary"
            size="md"
            block
            type="submit"
            data-testid="customer-receipt-login-cta"
            disabled={signIn.requestOtp.isPending}
          >
            {t('receipt.loginCta')}
          </Button>
        </form>
      ) : (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => void signIn.handleVerify(event)}
          noValidate
        >
          <FormErrorSummary errors={verifySummary} />
          <FormField
            id={CUSTOMER_SIGN_IN_FIELD_IDS.code}
            label={t('auth.codeLabel')}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={signIn.code}
            onChange={(event) => signIn.setCode(event.target.value)}
            invalid={Boolean(signIn.codeError)}
          />
          <Button
            intent="primary"
            size="md"
            block
            type="submit"
            data-testid="customer-receipt-login-verify"
            disabled={signIn.verifyOtp.isPending}
          >
            {t('auth.verifyAndContinue')}
          </Button>
          <Button intent="ghost" size="sm" type="button" onClick={signIn.resetOtpChallenge}>
            {t('auth.enterOtherContact')}
          </Button>
        </form>
      )}
    </Card>
  );
}

/**
 * G1 belt: never paint inline OTP under signed-in global chrome (R1 + token).
 */
export function ReceiptLoginUpsellCard(
  props: ReceiptLoginUpsellCardProps,
): JSX.Element | null {
  // G3: pass session (match QrPaymentView) — empty-access /me → soft reclaim.
  const { session } = useCustomerSession();
  if (
    !canMountEmbeddedSignInForm({
      session,
    })
  ) {
    return null;
  }
  return (
    <CustomerFormFieldBoundary>
      <ReceiptLoginUpsellCardBody {...props} />
    </CustomerFormFieldBoundary>
  );
}
